import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";
import { ShoppingBag, User, Menu, X, LogOut, ShieldCheck } from "lucide-react";

const navItems = [
  { key: "about", to: "/about" },
  { key: "zeroSpace", to: "/zero-space" },
  { key: "products", to: "/products" },
  { key: "angelina", to: "/angelina" },
  { key: "crystals", to: "/crystals" },
  { key: "booking", to: "/booking" },
  { key: "member", to: "/dashboard" },
];

export default function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 border-b transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-[#e5ebeb]"
          : "backdrop-blur-md bg-white/70 border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-4 flex items-center gap-8">
        <Logo size="md" />

        <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-[#003645]">
          {navItems.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `transition-colors duration-200 hover:text-[#00868e] ${
                  isActive ? "text-[#00868e]" : ""
                }`
              }
            >
              {t(`nav.${n.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <LanguageToggle />

          <Link
            to="/cart"
            className="relative inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#e5ebeb] bg-white text-[#003645] hover:border-[#00868e] hover:text-[#00868e] transition-colors duration-200"
            data-testid="cart-btn"
            aria-label={t("nav.cart")}
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#005870] text-white text-[10px] font-semibold flex items-center justify-center px-1"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to={user.role === "admin" ? "/admin/orders" : "/dashboard"}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-[#003645] hover:text-[#00868e] transition-colors duration-200"
                data-testid="header-user-link"
              >
                {user.role === "admin" ? <ShieldCheck size={16} /> : <User size={16} />}
                <span className="max-w-[8ch] truncate">{user.name}</span>
              </Link>
              <button
                onClick={() => { logout(); navigate("/"); }}
                className="p-2 rounded-full text-[#4a6e77] hover:text-[#003645] transition-colors duration-200"
                aria-label={t("nav.logout")}
                data-testid="logout-btn"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/login"
                className="px-4 py-2 rounded-full text-sm font-medium text-[#003645] hover:text-[#00868e] transition-colors duration-200"
                data-testid="header-login-btn"
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 rounded-full text-sm font-medium bg-[#005870] text-white hover:bg-[#003645] transition-colors duration-200"
                data-testid="header-register-btn"
              >
                {t("nav.register")}
              </Link>
            </div>
          )}

          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden p-2 rounded-full border border-[#e5ebeb] text-[#003645]"
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-[#e5ebeb] bg-white/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-3 text-sm font-medium text-[#003645]">
            {navItems.map((n) => (
              <NavLink
                key={n.key}
                to={n.to}
                onClick={() => setMenuOpen(false)}
                data-testid={`mobile-nav-${n.key}`}
                className={({ isActive }) =>
                  `py-1.5 ${isActive ? "text-[#00868e]" : ""}`
                }
              >
                {t(`nav.${n.key}`)}
              </NavLink>
            ))}
            <div className="pt-2 border-t border-[#e5ebeb] flex gap-3">
              {user ? (
                <>
                  <Link
                    to={user.role === "admin" ? "/admin/orders" : "/dashboard"}
                    className="flex-1 px-4 py-2 rounded-full text-center text-sm font-medium bg-[#005870] text-white"
                    onClick={() => setMenuOpen(false)}
                  >
                    {user.role === "admin" ? t("nav.admin") : t("nav.dashboard")}
                  </Link>
                  <button
                    onClick={() => { setMenuOpen(false); logout(); navigate("/"); }}
                    className="px-4 py-2 rounded-full text-sm font-medium border border-[#005870] text-[#005870]"
                  >
                    {t("nav.logout")}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex-1 px-4 py-2 rounded-full text-center text-sm font-medium border border-[#005870] text-[#005870]"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("nav.login")}
                  </Link>
                  <Link
                    to="/register"
                    className="flex-1 px-4 py-2 rounded-full text-center text-sm font-medium bg-[#005870] text-white"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("nav.register")}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
